import { parseFormattedNumber } from '@allesauto/utils/funcs';
import clsx from 'clsx';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import './range.css';

import InputField from '../inputField';

export interface RangeValue {
  min: number;
  max: number;
}

export interface RangeProps {
  id?: string;
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  onChange?: (value: RangeValue) => void;
  useFloatValues?: boolean;
  rangeLabelText: string;
  icon: ReactNode;
  className?: string;
  darkBg?: boolean;
  extraClass?: string;
}

export const enum RangeInputType {
  Max = 'max',
  Min = 'min',
}

const Range: FC<RangeProps> = ({
  id,
  min,
  max,
  valueMin,
  valueMax,
  onChange,
  useFloatValues = false,
  rangeLabelText,
  icon,
  className: customClassName,
  darkBg,
  extraClass,
  ...restProps
}) => {
  const intl = useIntl();

  const formatNumber = useCallback(
    (value: string | number) => {
      return intl.formatNumber(
        useFloatValues
          ? parseFloat(value.toString())
          : parseInt(value.toString())
      );
    },
    [intl, useFloatValues]
  );

  const inputMinRef = useRef(null);
  const inputMaxRef = useRef(null);
  const [inputMinValue, setInputMinValue] = useState(formatNumber(valueMin));
  const [inputMaxValue, setInputMaxValue] = useState(formatNumber(valueMax));

  const setInputValue = useCallback(
    (inputType: RangeInputType.Min | RangeInputType.Max): void => {
      const inputRef =
        inputType === RangeInputType.Min ? inputMinRef : inputMaxRef;
      const refElExists = inputRef && inputRef.current;
      const inputValue = refElExists
        ? (inputRef.current as unknown as HTMLInputElement).value
        : '';

      if (refElExists && inputType === RangeInputType.Min) {
        setInputMinValue(inputValue);
      }

      if (refElExists && inputType === RangeInputType.Max) {
        setInputMaxValue(inputValue);
      }
    },
    []
  );

  useEffect(() => {
    setInputMinValue(formatNumber(valueMin));
  }, [formatNumber, valueMin]);

  useEffect(() => {
    setInputMaxValue(formatNumber(valueMax));
  }, [formatNumber, valueMax]);

  const onRangeChange = useCallback(
    (
      value: string | number,
      inputType: RangeInputType.Min | RangeInputType.Max
    ) => {
      const newValue: number =
        typeof value === 'number' ? value : parseFormattedNumber(intl, value);
      const inputMinValueParsed: number = parseFormattedNumber(
        intl,
        inputMinValue
      );
      const inputMaxValueParsed: number = parseFormattedNumber(
        intl,
        inputMaxValue
      );

      // if new values is valid
      if (newValue >= min && newValue <= max) {
        if (inputType === RangeInputType.Min) {
          const newMinValue =
            newValue <= inputMaxValueParsed ? newValue : valueMin;

          if (onChange) {
            onChange({
              min: newMinValue,
              max: inputMaxValueParsed,
            });
          }
        } else {
          const newMaxValue =
            newValue >= inputMinValueParsed ? newValue : valueMax;

          if (onChange) {
            onChange({
              min: inputMinValueParsed,
              max: newMaxValue,
            });
          }
        }
      } else {
        if (inputType === RangeInputType.Min) {
          if (onChange) {
            onChange({
              min,
              max: inputMaxValueParsed,
            });
          }
        } else {
          if (onChange) {
            onChange({
              min: inputMinValueParsed,
              max,
            });
          }
        }
      }
    },
    [intl, min, max, valueMin, valueMax, inputMinValue, inputMaxValue, onChange]
  );

  const className = {
    range: clsx(
      'content-center grid items-center justify-center range',
      customClassName
    ),
    icon: clsx(
      'absolute bottom-[15px] right-20px',
      darkBg
        ? ' text-green60 monochrome:text-white'
        : ' text-purple80 monochrome:text-grey90'
    ),
    rangeText: clsx(
      darkBg ? 'text-white' : 'text-purple80 monochrome:text-grey90',
      ' text-center'
    ),
  };

  return (
    <>
      <div className={className.range}>
        <div className="range-input-field">
          <InputField
            id={`${id}-min`}
            darkBg={darkBg}
            value={inputMinValue}
            {...restProps}
            ref={inputMinRef}
            type="text"
            onChange={() => setInputValue(RangeInputType.Min)}
            onBlur={(e) =>
              onRangeChange(e.currentTarget.value, RangeInputType.Min)
            }
            onKeyDown={(e) =>
              (e.key || '').toUpperCase() === 'ENTER'
                ? onRangeChange(e.currentTarget.value, RangeInputType.Min)
                : null
            }
            icon={icon}
            extraClass={extraClass}
          />
        </div>
        <span className={className.rangeText}>{rangeLabelText}</span>
        <div className="range-input-field">
          <InputField
            id={`${id}-max`}
            darkBg={darkBg}
            type="text"
            value={inputMaxValue}
            {...restProps}
            ref={inputMaxRef}
            onChange={() => setInputValue(RangeInputType.Max)}
            onKeyDown={(e) =>
              (e.key || '').toUpperCase() === 'ENTER'
                ? onRangeChange(e.currentTarget.value, RangeInputType.Max)
                : null
            }
            onBlur={(e) =>
              onRangeChange(e.currentTarget.value, RangeInputType.Max)
            }
            icon={icon}
            extraClass={extraClass}
          />
        </div>
      </div>
    </>
  );
};

export default Range;
